/*
  Image slider gallery.

    - Default mode: native horizontal scroll + scroll-snap, with chevron
      buttons that scrollBy() one slide at a time. Loop wraps via JS.
    - Autoscroll mode: continuous marquee. JS duplicates every slide so the
      track contains 2N slides; CSS animates translateX(0 → -50%) on a loop.
      Arrows are hidden (see CSS) because discrete jumps don't fit a
      continuous animation. prefers-reduced-motion disables marquee setup.

  Plain JS, no dependencies. Re-runs on Shopify section editor load events.
*/
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  function init(section) {
    var track = section.querySelector('[data-track]');
    var prev = section.querySelector('[data-prev]');
    var next = section.querySelector('[data-next]');
    if (!track) return;
    if (section.dataset.galleryWired === '1') return;
    section.dataset.galleryWired = '1';

    var loop = section.dataset.loop === 'true';
    var autoscroll = section.dataset.autoscroll === 'true' && !REDUCED_MOTION;
    var intervalSec = parseInt(section.dataset.autoscrollInterval, 10);
    if (!isFinite(intervalSec) || intervalSec < 1) intervalSec = 5;
    var slideCount = parseInt(section.dataset.slideCount, 10) || 0;

    /* ---------- Marquee mode (autoscroll) ----------
       Skip arrow/scroll wiring entirely — CSS animation drives motion. */
    if (autoscroll && slideCount > 1) {
      setupMarquee(section, track, slideCount, intervalSec);
      return;
    }

    /* ---------- Default mode (arrows + scroll) ---------- */

    function getStep() {
      var slide = track.querySelector('.image-slider-gallery__slide');
      if (!slide) return 0;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function go(direction) {
      var step = getStep();
      if (!step) return;
      var max = maxScroll();
      var current = track.scrollLeft;
      if (direction > 0) {
        if (loop && current >= max - step / 2) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: step, behavior: 'smooth' });
        }
      } else {
        if (loop && current <= step / 2) {
          track.scrollTo({ left: max, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: -step, behavior: 'smooth' });
        }
      }
    }

    function updateArrowState() {
      if (!prev || !next) return;
      if (loop) {
        prev.disabled = false;
        next.disabled = false;
        return;
      }
      var max = maxScroll() - 1;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max;
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    track.addEventListener('scroll', updateArrowState, { passive: true });
    window.addEventListener('resize', updateArrowState);
    updateArrowState();
  }

  function setupMarquee(section, track, slideCount, intervalSec) {
    // Clone every original slide and append to the track. With flex `gap`,
    // the original→clone boundary inherits the same spacing, and the CSS
    // sets `padding-right: var(--gallery-gap)` so a -50% translation lands
    // exactly on the first clone (visually equal to the first original).
    var slides = track.querySelectorAll('.image-slider-gallery__slide');
    for (var i = 0; i < slides.length; i++) {
      var clone = slides[i].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.marqueeClone = '1';
      // Disable focusable descendants in clones so tab order isn't doubled.
      var focusables = clone.querySelectorAll('a, button, input, [tabindex]');
      for (var j = 0; j < focusables.length; j++) {
        focusables[j].setAttribute('tabindex', '-1');
      }
      track.appendChild(clone);
    }
    // Speed = one slide-width (incl. gap) per intervalSec. The animation
    // covers slideCount slides over its duration → duration = N * interval.
    var duration = slideCount * intervalSec;
    section.style.setProperty('--marquee-duration', duration + 's');
    section.classList.add('is-marquee');
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('.image-slider-gallery');
    for (var i = 0; i < sections.length; i++) init(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
